import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';

import useUI from 'src/hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import { useHeader } from 'src/dispensary/core-menu/components/header/use-header';

import { CartV2 } from 'src/assets/cart-v2';
import { VisuallyHidden } from 'src/components/core/visually-hidden';
import { buttonResetStyles } from 'src/dispensary/core-menu/components/header/header.styles';

export const CartButton = observer((): JSX.Element | null => {
  const UI = useUI();
  const { itemCount } = useCart();
  const { trackEvent } = useHeader();

  if (UI.viewOnlyMode) {
    return null;
  }

  const handleClick = (): void => {
    UI.openCartDrawer();
    trackEvent('open cart');
  };

  return (
    <Button onClick={handleClick} data-testid='cartButton' $hasItems={itemCount > 0}>
      <ButtonWrapper>
        <CartV2 />
        <span data-testid='cart-item-count'>{itemCount}</span>
        <VisuallyHidden>item{itemCount === 1 ? '' : 's'} in your shopping cart</VisuallyHidden>
      </ButtonWrapper>
    </Button>
  );
});

const Button = styled.button<{ $hasItems: boolean }>`
  ${buttonResetStyles}

  border-radius: var(--dcm-cart-btn-border-radius);
  flex: 0 0 var(--dcm-cart-btn-width);
  font-weight: var(--dcm-cart-btn-font-weight);
  height: var(--dcm-cart-btn-height);
  overflow: hidden;
  width: var(--dcm-cart-btn-width);

  ${({ $hasItems }) =>
    $hasItems
      ? css`
          background: var(--dcm-cart-btn-bg-active);
          color: var(--dcm-cart-btn-text-active);
        `
      : css`
          background: var(--dcm-cart-btn-bg-empty);
          color: var(--dcm-cart-btn-text-empty);
        `}

  @media (max-width: 879px) {
    svg {
      height: 20px;
      width: 20px;
    }
  }

  @media (min-width: 880px) {
    flex: 0 0 var(--dcm-cart-btn-width-desktop);
    height: var(--dcm-cart-btn-height-desktop);
    width: var(--dcm-cart-btn-width-desktop);
  }
`;

const ButtonWrapper = styled.span`
  align-items: center;
  background-blend-mode: multiply;
  background: none;
  display: flex;
  height: 100%;
  justify-content: space-between;
  padding: var(--dcm-cart-btn-padding);
  transition: var(--dcm-cart-btn-transition);
  width: 100%;

  @media (min-width: 880px) {
    padding: var(--dcm-cart-btn-padding-desktop);
  }

  &:hover {
    background: var(--dcm-cart-btn-hover-bg);
  }
`;
